import { deriveReelSections } from './deriveReelSections';
import { deriveReelChapterGroups } from './reelChapters';
import { reelSectionSegmentIdSets } from './reelSectionTranscriptIndex';

import type { ReelSection } from './deriveReelSections';
import type { Reel } from './reelTypes';
import type { Timeline } from '@src/editor/models/Timeline';

/**
 * PLAT-11156 — the reel's structure resolved down to the transcript's words.
 *
 * The Reel Editor knows a reel's Sections and Chapters; the transcript knows
 * the words. This module is the one place that connects them: the same
 * derivations the Reel Editor renders from (DE1 — `deriveReelSections` +
 * `deriveReelChapterGroups`, so the two views cannot disagree), plus a per-word
 * Section stamp resolved through the same clip-segment hop PLAT-11061's jump
 * uses (DE3), generalised from one index to all of them.
 *
 * Pure and store-free like its siblings: a timeline, the stored reel and the
 * token array in, plain data out. No cache — deleting a word or reordering a
 * Section changes the next call's answer with nothing to invalidate; the host
 * memoizes on its own observable inputs.
 */

/** Only the fields the stamp reads — `getFullTranscript()`'s token shape. */
export type StructureTokenLike = {
  type?: string;
  subtype?: string;
  state?: string;
  clip_segment_id?: string;
  /**
   * Timeline time for text tokens; punctuation carries none, and a removed
   * element's is `null` at runtime whatever the wider types say.
   */
  ts?: number | null;
};

export type ReelTranscriptSection = {
  sectionId: string;
  /** `storedName || openingWords` — the Reel Editor's exact fallback (DE8). */
  displayName: string;
  /**
   * The two halves of that fallback, kept apart for the rename field: it edits
   * the stored name and shows the opening words as its placeholder, so clearing
   * it means "no name" rather than "the opening words, typed by hand"
   * (PLAT-11054 DS1, the Reel Editor row's own split).
   */
  storedName?: string;
  openingWords: string;
  /** Position in playback order, from 0. */
  index: number;
  startTime: number;
  duration: number;
  speakerLabels: string[];
};

export type ReelTranscriptChapter = {
  chapterId: string;
  /** `''` when untitled — the renderer falls back to `Chapter {ordinal}`. */
  title: string;
  /** 1-based, same numbering as the Reel Editor's chapter rows. */
  ordinal: number;
  sections: ReelTranscriptSection[];
  /** Sum of its Sections' durations, in seconds. */
  duration: number;
};

export type ReelTranscriptStructure = {
  /** Empty when the reel has no chapters — the flat list is the default. */
  chapters: ReelTranscriptChapter[];
  /** Every live Section, flat, in playback order. */
  sections: ReelTranscriptSection[];
  /** Sum of the live Sections' durations, in seconds. */
  totalDuration: number;
};

export const EMPTY_REEL_TRANSCRIPT_STRUCTURE: ReelTranscriptStructure = {
  chapters: [],
  sections: [],
  totalDuration: 0,
};

/** Matches `MEDIA_CONTIGUITY_TOLERANCE`'s reading of "the same instant". */
const BOUNDARY_TOLERANCE = 1e-3;

const isActive = (token: StructureTokenLike) =>
  token.state !== 'removed' && token.state !== 'cut';

/**
 * Resolves the reel's structure — its Sections and Chapters, with names,
 * order and durations. Deliberately carries **no word indices**: index maps
 * live only in {@link ReelWordStamp}, produced by
 * {@link stampReelTranscriptWords} against the caller's own token array, so
 * there is exactly one index space in play and it is always the renderer's.
 *
 * Returns {@link EMPTY_REEL_TRANSCRIPT_STRUCTURE} when there is no stored reel
 * or the timeline yields no Sections — a clip that is not a reel produces no
 * structure rather than one row. Sections the creator has turned off have no
 * elements on the timeline and are absent by construction.
 */
export function deriveReelTranscriptStructure(
  timeline: Timeline,
  reel: Reel | undefined,
): ReelTranscriptStructure {
  if (!reel) return EMPTY_REEL_TRANSCRIPT_STRUCTURE;

  const derived = deriveReelSections(timeline);
  if (derived.length === 0) return EMPTY_REEL_TRANSCRIPT_STRUCTURE;

  const sections = derived.map<ReelTranscriptSection>(section => ({
    sectionId: section.id,
    displayName: reel.sections[section.id]?.name || section.openingWords,
    storedName: reel.sections[section.id]?.name,
    openingWords: section.openingWords,
    index: section.index,
    startTime: section.startTime,
    duration: section.duration,
    speakerLabels: section.speakerLabels,
  }));

  // Grouped over the derived rows — the same call the Reel Editor makes — and
  // mapped back to this module's rows by position, which the grouping keeps.
  const sectionsById = new Map(
    sections.map(section => [section.sectionId, section]),
  );
  const chapters = deriveReelChapterGroups(
    derived,
    reel.chapters,
  ).map<ReelTranscriptChapter>(group => {
    const rows = group.rows.flatMap(row => sectionsById.get(row.id) ?? []);
    return {
      chapterId: group.chapter.id,
      title: group.chapter.title,
      ordinal: group.ordinal,
      sections: rows,
      duration: rows.reduce((sum, row) => sum + row.duration, 0),
    };
  });

  return {
    chapters,
    sections,
    totalDuration: sections.reduce((sum, row) => sum + row.duration, 0),
  };
}

/** {@link stampReelTranscriptWords}' answer, in the caller's own index space. */
export type ReelWordStamp = {
  /** Token index (into the input array) → owning Section id. */
  sectionIdByWordIndex: ReadonlyMap<number, string>;
  /** Section id → its first stamped word's index. Absent = no word on page. */
  firstWordIndexBySectionId: ReadonlyMap<string, number>;
};

/**
 * The per-word stamp alone, against whatever token array the caller renders.
 *
 * The transcript views render a *projection* of `getFullTranscript()`
 * (`dedupeConsecutiveWhitespace` drops a token at section seams — see
 * `useTranscriptIndexJump.resolveIndex`), so an index map derived from one
 * array is off by one per seam in the other. A renderer therefore stamps its
 * own array with this rather than borrowing indices computed elsewhere; the
 * stamp is segment-membership based, so it lands identically on any
 * projection of the same tokens.
 */
export function stampReelTranscriptWords(
  timeline: Timeline,
  transcriptElements: readonly StructureTokenLike[] | undefined,
): ReelWordStamp {
  const sections = deriveReelSections(timeline);
  const { sectionIdByWordIndex, firstWordIndexes } = stampWords(
    timeline,
    sections,
    transcriptElements,
  );
  const firstWordIndexBySectionId = new Map<string, number>();
  sections.forEach((section, position) => {
    const index = firstWordIndexes[position];
    if (index !== undefined) firstWordIndexBySectionId.set(section.id, index);
  });
  return { sectionIdByWordIndex, firstWordIndexBySectionId };
}

/**
 * One pass over the tokens: text words stamp by segment membership, everything
 * else inherits the stamp of the word before it. `firstWordIndexes` records
 * each Section's opening word in the same pass — per Section it agrees with
 * {@link reelSectionTranscriptIndex}, which the jump keeps using for one
 * Section at a time.
 */
function stampWords(
  timeline: Timeline,
  sections: readonly ReelSection[],
  transcriptElements: readonly StructureTokenLike[] | undefined,
) {
  const sectionIdByWordIndex = new Map<number, string>();
  const firstWordIndexes: (number | undefined)[] = sections.map(
    () => undefined,
  );
  if (!transcriptElements?.length) {
    return { sectionIdByWordIndex, firstWordIndexes };
  }

  // Segment id → the Sections whose elements carry it, in playback order.
  // Almost always one Section; two means the same source passage is used
  // twice.
  const segmentIdSets = reelSectionSegmentIdSets(
    timeline,
    sections.map(section => section.elementIds),
  );
  const positionsBySegmentId = new Map<string, number[]>();
  segmentIdSets.forEach((segmentIds, position) => {
    for (const segmentId of segmentIds) {
      const positions = positionsBySegmentId.get(segmentId);
      if (positions) positions.push(position);
      else positionsBySegmentId.set(segmentId, [position]);
    }
  });

  let lastWordSectionId: string | undefined;
  transcriptElements.forEach((token, index) => {
    if (!isActive(token)) return;

    if (token.type !== 'text') {
      // Punctuation and whitespace ride with the word before them — a
      // Section's trailing full stop folds away with its passage, and a token
      // ahead of every word stays unowned.
      if (lastWordSectionId !== undefined) {
        sectionIdByWordIndex.set(index, lastWordSectionId);
      }
      return;
    }

    const positions =
      token.clip_segment_id !== undefined
        ? positionsBySegmentId.get(token.clip_segment_id)
        : undefined;
    if (!positions) {
      lastWordSectionId = undefined;
      return;
    }

    const position =
      positions.length === 1
        ? positions[0]
        : positionAtTime(positions, sections, token.ts);
    sectionIdByWordIndex.set(index, sections[position].id);
    firstWordIndexes[position] ??= index;
    lastWordSectionId = sections[position].id;
  });

  return { sectionIdByWordIndex, firstWordIndexes };
}

/**
 * The twice-used passage: among the Sections sharing the word's segment id,
 * the last one that has started at the word's `ts` — the same reading of a
 * boundary instant as `sectionIdAtTime`.
 */
function positionAtTime(
  positions: readonly number[],
  sections: readonly ReelSection[],
  ts: number | null | undefined,
): number {
  if (typeof ts !== 'number') return positions[0];
  for (let i = positions.length - 1; i >= 0; i--) {
    if (sections[positions[i]].startTime <= ts + BOUNDARY_TOLERANCE) {
      return positions[i];
    }
  }
  return positions[0];
}
